import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  Avatar,
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  ExitToApp as ExitToAppIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNotification } from '@/common/components/notification/NotificationContext';
import {
  useRemoveProjectMemberMutation,
  useLeaveProjectMutation,
  useGetProjectInvitesQuery,
  ProjectInviteType,
  useProjectInviteCancelMutation,
  ProjectInviteCancelInput,
} from '@/generated/graphql';
import { formatDate } from '@/common/utils/fileUtils';
import { useAuth } from '@/common/auth/hooks/useAuth';
import { InviteMemberDialog } from '../components/InviteMemberDialog';
import { ProjectInviteStatus } from '@/common/models/enums';
import {
  getProjectMemberRoleColor,
  getProjectInviteStatusColor,
} from '@/common/utils/statusUtils';
import { useProjectDetails } from '../context/ProjectDetailsContext';
import { useProjectPermissions } from '@/hooks/projectPermissions';

interface ProjectMembersTabProps {
  onClose: () => void;
}

const ProjectMembersTab: React.FC<ProjectMembersTabProps> = ({ onClose }) => {
  const { project, onProjectUpdated, refetchProject } = useProjectDetails();
  const { userId: currentUserId, userEmail: currentUserEmail } = useAuth();
  const {
    canInvite,
    canLeave,
    canViewInvites,
    invitableRoles,
    canRemoveMember,
    canCancelInvite,
  } = useProjectPermissions(project);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const { showSuccess, showError } = useNotification();
  const [removeProjectMember, { loading: removeLoading }] =
    useRemoveProjectMemberMutation();
  const [cancelInvite] = useProjectInviteCancelMutation();
  const [leaveProject, { loading: leaveLoading }] = useLeaveProjectMutation();

  const {
    data: invitesData,
    loading: invitesLoading,
    refetch: refetchInvites,
  } = useGetProjectInvitesQuery({
    variables: { projectId: project?._id ?? '' },
    skip: !project || !canViewInvites,
  });

  if (!project) return null;

  const handleRemoveMember = async (memberId: string, memberEmail: string) => {
    if (
      !window.confirm(
        `Are you sure you want to remove ${memberEmail} from this project?`
      )
    ) {
      return;
    }

    try {
      await removeProjectMember({
        variables: {
          data: {
            projectId: project._id,
            memberId,
          },
        },
      });
      showSuccess('Member removed successfully');
      await refetchProject();
      onProjectUpdated();
    } catch (error) {
      console.error('Error removing member:', error);
      const message =
        error instanceof Error ? error.message : 'Failed to remove member';
      showError(message);
    }
  };

  const handleCancelInvite = async (
    invitedEmail: ProjectInviteType['email'],
    inviteId: ProjectInviteType['_id']
  ) => {
    if (
      !window.confirm(
        `Are you sure you want to cancel the invite to ${invitedEmail}?`
      )
    ) {
      return;
    }

    try {
      await cancelInvite({
        variables: {
          data: {
            inviteId: inviteId,
          } as ProjectInviteCancelInput,
        },
      });
      refetchInvites();
    } catch (error) {
      console.error('Error canceling invite:', error);
      const message =
        error instanceof Error ? error.message : 'Failed to cancel invite';
      showError(message);
    }
  };

  const handleLeaveProject = async () => {
    if (!window.confirm('Are you sure you want to leave this project?')) {
      return;
    }

    try {
      await leaveProject({
        variables: { projectId: project._id },
      });
      onClose();
      showSuccess('Left project successfully');
      onProjectUpdated();
    } catch (error) {
      console.error('Error leaving project:', error);
      const message =
        error instanceof Error ? error.message : 'Failed to leave project';
      showError(message);
    }
  };

  const hasInvites = invitesData?.projectInvitesSent
    ? invitesData.projectInvitesSent.length > 0
    : false;

  return (
    <Box>
      {/* Header with invite button */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6">Project Members</Typography>
        {canInvite && (
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite Member
          </Button>
        )}
      </Box>

      {/* Current Members */}
      <Box sx={{ mb: 3 }} data-cy="currentMembersBox">
        <Box p={2}>
          <Typography variant="subtitle1" gutterBottom>
            Current Members ({project.members?.length || 0})
          </Typography>
          {project.members && project.members.length > 0 ? (
            <List>
              {project.members.map((member, index) => (
                <React.Fragment key={member._id}>
                  <ListItem>
                    <Avatar sx={{ mr: 2 }}>
                      <PersonIcon />
                    </Avatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          component="span"
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          {member.artistName}
                          <Chip
                            label={member.role}
                            size="small"
                            color={getProjectMemberRoleColor(member.role)}
                          />
                        </Typography>
                      }
                      secondary={
                        <Box component="span">
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            display="block"
                          >
                            {member.userEmail}
                          </Typography>
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Joined: {formatDate(member.joinedAt)}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {canRemoveMember(member.role) && (
                        <IconButton
                          edge="end"
                          onClick={() =>
                            handleRemoveMember(member._id, member.userEmail)
                          }
                          disabled={removeLoading}
                          title="Remove Member"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                      {canLeave && member.userId === currentUserId && (
                        <IconButton
                          edge="end"
                          onClick={handleLeaveProject}
                          disabled={leaveLoading}
                          data-cy="leaveProjectButton"
                          title="Leave Project"
                        >
                          <ExitToAppIcon />
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < project.members.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              py={2}
            >
              No members found
            </Typography>
          )}
        </Box>
      </Box>

      {/* Invitation History (owner only) */}
      {canViewInvites && (
        <Box aria-label="Invitation History" data-cy="invitationHistoryBox">
          <Box p={2}>
            <Typography variant="subtitle1" gutterBottom>
              Invitation History
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Complete history of all invitation attempts and outcomes
            </Typography>
            {invitesLoading ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : hasInvites ? (
              <List>
                {Array.isArray(invitesData?.projectInvitesSent)
                  ? [...invitesData.projectInvitesSent]
                      ?.sort(
                        (a, b) =>
                          new Date(b.createdAt).getTime() -
                          new Date(a.createdAt).getTime()
                      )
                      .map((invite) => (
                        <ListItem key={invite._id}>
                          <Avatar sx={{ mr: 2 }}>
                            <EmailIcon />
                          </Avatar>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body1"
                                component="span"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                {invite.email}
                                <Chip
                                  label={invite.status}
                                  size="small"
                                  color={getProjectInviteStatusColor(
                                    invite.status
                                  )}
                                />
                              </Typography>
                            }
                            secondary={
                              <Box component="span">
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Invited: {formatDate(invite.createdAt)}
                                </Typography>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  Expires: {formatDate(invite.expiresAt)}
                                </Typography>
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.7rem',
                                  }}
                                >
                                  ID: {invite._id.slice(-8)} • Role:{' '}
                                  {invite.role}
                                </Typography>
                              </Box>
                            }
                          />
                          {canCancelInvite(invite.role) &&
                            invite.status === ProjectInviteStatus.PENDING && (
                              <IconButton
                                edge="end"
                                onClick={() =>
                                  handleCancelInvite(invite.email, invite._id)
                                }
                                disabled={removeLoading}
                                title="Cancel Invitation"
                                data-cy={`invite-cancel-${invite._id}`}
                              >
                                <CancelIcon />
                              </IconButton>
                            )}
                        </ListItem>
                      ))
                  : null}
              </List>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                py={2}
              >
                No invitations sent yet
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <InviteMemberDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        projectId={project._id}
        currentUserEmail={currentUserEmail}
        invitableRoles={invitableRoles}
        existingInvites={
          invitesData?.projectInvitesSent?.map((invite) => ({
            email: invite.email,
            status: invite.status,
          })) || []
        }
        existingMembers={
          project.members?.map((member) => ({
            userEmail: member.userEmail,
          })) || []
        }
        onSuccess={() => {
          refetchInvites();
        }}
      />
    </Box>
  );
};

export default ProjectMembersTab;
